import { getIn } from 'formik';
import { useEffect, useState } from 'react';
import { AnyObjectSchema } from 'yup';
import { isRequiredField } from '../generic/crud/utils/isRequiredField';

/**
 * Custom hook to divide fields of a Yup validation schema into required and optional sections,
 * considering top-level fields and treating nested fields as single entities. If any field
 * within a nested structure is required, the entire nested object is considered required;
 * otherwise, it is considered optional.
 *
 * @param validationSchema The Yup validation schema.
 * @returns An object with two properties: requiredFields and optionalFields, each an array
 *          of field names or paths. Nested structures are treated as single entities based
 *          on the presence of any required field within them.
 */
export const useDivideSchemaFields = (
  validationSchema: AnyObjectSchema
): { requiredFields: string[]; optionalFields: string[] } => {
  const [sections, setSections] = useState<{
    requiredFields: string[];
    optionalFields: string[];
  }>({ requiredFields: [], optionalFields: [] });

  useEffect(() => {
    const schemaDescription = validationSchema.describe();
    const requiredFields: string[] = [];
    const optionalFields: string[] = [];

    Object.keys(schemaDescription.fields).forEach((fieldName) => {
      // Directly use isRequiredField to check if a top-level field is required
      if (isRequiredField(validationSchema, fieldName)) {
        requiredFields.push(fieldName);
      } else {
        optionalFields.push(fieldName);
      }

      // For nested fields, determine if the nested structure contains any required field
      const fieldSchema = getIn(validationSchema.fields, fieldName);
      if (fieldSchema && fieldSchema.describe().fields) {
        const hasRequiredField = Object.keys(
          fieldSchema.describe().fields
        ).some((nestedFieldName) =>
          isRequiredField(validationSchema, `${fieldName}.${nestedFieldName}`)
        );

        // If the nested structure contains a required field, treat the entire nested object as required
        if (hasRequiredField) {
          if (!requiredFields.includes(fieldName)) {
            requiredFields.push(fieldName);
            // Remove from optionalFields if it was previously added
            const index = optionalFields.indexOf(fieldName);
            if (index > -1) {
              optionalFields.splice(index, 1);
            }
          }
        } else if (
          !optionalFields.includes(fieldName) &&
          !requiredFields.includes(fieldName)
        ) {
          optionalFields.push(fieldName);
        }
      }
    });

    setSections({ requiredFields, optionalFields });
  }, [validationSchema]);

  return sections;
};
